import Vue from 'vue'

/**
 * Date mask directive for yyyy.mm.dd format
 * Auto-inserts dots as user types, handles paste normalization, preserves caret position
 */
Vue.directive('date-mask', {
  bind(el, binding, vnode) {
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input) return

    // Store original handlers to avoid conflicts
    input._dateMaskHandlers = {
      input: handleInput.bind(null, input, vnode),
      paste: handlePaste.bind(null, input, vnode),
      blur: handleBlur.bind(null, input, vnode)
    }

    input.addEventListener('input', input._dateMaskHandlers.input)
    input.addEventListener('paste', input._dateMaskHandlers.paste)
    input.addEventListener('blur', input._dateMaskHandlers.blur)
  },

  unbind(el) {
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input || !input._dateMaskHandlers) return

    input.removeEventListener('input', input._dateMaskHandlers.input)
    input.removeEventListener('paste', input._dateMaskHandlers.paste)
    input.removeEventListener('blur', input._dateMaskHandlers.blur)
    delete input._dateMaskHandlers
  }
})

function handleInput(input, vnode, event) {
  const cursorPos = input.selectionStart
  const value = input.value
  
  // Only allow digits and dots
  const digitsOnly = value.replace(/[^\d]/g, '')
  
  // Limit to 8 digits (YYYYMMDD)
  const limitedDigits = digitsOnly.slice(0, 8)
  
  // Extract year, month, day parts
  let year = limitedDigits.slice(0, 4)
  let month = limitedDigits.slice(4, 6)
  let day = limitedDigits.slice(6, 8)
  
  // Validate and correct month (1-12)
  if (month.length === 1) {
    // Single digit - allow as is
  } else if (month.length === 2) {
    const monthNum = parseInt(month, 10)
    if (monthNum > 12) {
      month = '12'
    } else if (monthNum < 1) {
      month = '01'
    } else {
      // Ensure proper formatting (e.g., '01' instead of '1')
      month = monthNum.toString().padStart(2, '0')
    }
  }
  
  // Validate and correct day (1-31)
  if (day.length === 1) {
    // Single digit - allow as is
  } else if (day.length === 2) {
    const dayNum = parseInt(day, 10)
    if (dayNum > 31) {
      day = '31'
    } else if (dayNum < 1) {
      day = '01'
    } else {
      // Ensure proper formatting (e.g., '01' instead of '1')
      day = dayNum.toString().padStart(2, '0')
    }
  }
  
  // Reconstruct the validated digits
  const validatedDigits = year + (month || '') + (day || '')
  
  // Format with dots: YYYY.MM.DD
  let formatted = validatedDigits
  if (validatedDigits.length > 4) {
    formatted = validatedDigits.slice(0, 4) + '.' + validatedDigits.slice(4, 6)
  }
  if (validatedDigits.length > 6) {
    formatted = validatedDigits.slice(0, 4) + '.' + validatedDigits.slice(4, 6) + '.' + validatedDigits.slice(6)
  }
  
  // Update input value
  input.value = formatted
  
  // Calculate new cursor position
  let newCursorPos = cursorPos
  if (formatted.length > value.length) {
    // Dot was inserted or digits were corrected, adjust cursor
    newCursorPos = Math.min(cursorPos + (formatted.length - value.length), formatted.length)
  } else if (formatted.length < value.length) {
    // Characters were removed, adjust cursor
    newCursorPos = Math.max(cursorPos - (value.length - formatted.length), 0)
  }
  
  // Set cursor position after DOM update
  setTimeout(() => {
    input.setSelectionRange(newCursorPos, newCursorPos)
  }, 0)
  
  // Update v-model
  updateVModel(vnode, formatted)
}

function handlePaste(input, vnode, event) {
  event.preventDefault()
  
  const pastedText = (event.clipboardData || window.clipboardData).getData('text')
  const normalizedDate = normalizePastedDate(pastedText)
  
  if (normalizedDate) {
    input.value = normalizedDate
    updateVModel(vnode, normalizedDate)
    
    // Set cursor to end
    setTimeout(() => {
      input.setSelectionRange(normalizedDate.length, normalizedDate.length)
    }, 0)
  }
}

function handleBlur(input, vnode, event) {
  const value = input.value.trim()
  
  // If fully typed (10 chars) but invalid date, show toast
  if (value.length === 10 && !isValidDate(value)) {
    // Show toast notification
    if (vnode.context && vnode.context.$store && vnode.context.$store.dispatch) {
      vnode.context.$store.dispatch('toast/push', {
        message: 'Please enter a valid date in yyyy.mm.dd format (e.g., 1987.03.16)',
        type: 'error'
      })
    }
    // Keep the value for user correction - don't clear it
  }
}

function normalizePastedDate(text) {
  // Extract digits from pasted text
  const digits = text.replace(/[^\d]/g, '')
  
  // Try to parse common date formats
  const formats = [
    /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/, // YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD
    /^(\d{1,2})[-./](\d{1,2})[-./](\d{4})$/, // DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY
    /^(\d{1,2})[-./](\d{1,2})[-./](\d{2})$/, // DD-MM-YY, DD/MM/YY, DD.MM.YY
  ]
  
  for (const format of formats) {
    const match = text.match(format)
    if (match) {
      let [, part1, part2, part3] = match
      
      // Determine if it's YYYY-MM-DD or DD-MM-YYYY format
      if (part1.length === 4) {
        // YYYY-MM-DD format
        const year = part1
        
        // Validate month (1-12)
        let month = parseInt(part2, 10)
        if (month > 12) month = 12
        if (month < 1) month = 1
        month = month.toString().padStart(2, '0')
        
        // Validate day (1-31)
        let day = parseInt(part3, 10)
        if (day > 31) day = 31
        if (day < 1) day = 1
        day = day.toString().padStart(2, '0')
        
        return `${year}.${month}.${day}`
      } else {
        // DD-MM-YYYY or DD-MM-YY format
        // Validate day (1-31)
        let day = parseInt(part1, 10)
        if (day > 31) day = 31
        if (day < 1) day = 1
        day = day.toString().padStart(2, '0')
        
        // Validate month (1-12)
        let month = parseInt(part2, 10)
        if (month > 12) month = 12
        if (month < 1) month = 1
        month = month.toString().padStart(2, '0')
        
        let year = part3
        
        // Handle 2-digit years
        if (year.length === 2) {
          const currentYear = new Date().getFullYear()
          const currentCentury = Math.floor(currentYear / 100) * 100
          year = currentCentury + parseInt(year)
          
          // If the year is more than 50 years in the future, assume previous century
          if (year - currentYear > 50) {
            year -= 100
          }
        }
        
        return `${year}.${month}.${day}`
      }
    }
  }
  
  // Fallback: if we have 8 digits, format as YYYYMMDD
  if (digits.length === 8) {
    const year = digits.slice(0, 4)
    
    // Validate month (1-12)
    let month = parseInt(digits.slice(4, 6), 10)
    if (month > 12) month = 12
    if (month < 1) month = 1
    month = month.toString().padStart(2, '0')
    
    // Validate day (1-31)
    let day = parseInt(digits.slice(6, 8), 10)
    if (day > 31) day = 31
    if (day < 1) day = 1
    day = day.toString().padStart(2, '0')
    
    return `${year}.${month}.${day}`
  }
  
  return null
}

function isValidDate(dateString) {
  // Check format: YYYY.MM.DD
  const match = dateString.match(/^(\d{4})\.(\d{2})\.(\d{2})$/)
  if (!match) return false
  
  const [, year, month, day] = match
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  
  // Check if the date is valid and matches the input
  return date.getFullYear() == year &&
         date.getMonth() == month - 1 &&
         date.getDate() == day
}

function updateVModel(vnode, value) {
  // Update v-model value
  if (vnode.componentInstance) {
    // For components like o-input
    vnode.componentInstance.$emit('input', value)
  } else if (vnode.data && vnode.data.model) {
    // For direct v-model on input
    vnode.data.model.callback(value)
  }
}
